import apiClient from '../lib/api-client';

export interface Plan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: string;
  features: string[];
  stripePriceId?: string;
  popular?: boolean;
}

export interface Subscription {
  plan: string | null;
  status: string | null;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  passTier?: string | null;
  passStartedAt?: string | null;
  passExpiresAt?: string | null;
  passActive?: boolean;
}

export interface CheckoutResponse {
  url: string;
}

export interface PassCheckoutResponse {
  url: string;
}

export interface ClaimPassResponse {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    emailVerified?: boolean;
  };
  accessToken: string;
  refreshToken: string;
}

export interface Invoice {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: string;
  pdfUrl: string | null;
  hostedUrl: string | null;
  description: string;
}

export const subscriptionService = {
  async getPlans() {
    const response = await apiClient.get<Plan[]>('/subscriptions/plans');
    return response.data;
  },

  async getStatus() {
    const response = await apiClient.get<Subscription>('/subscriptions/status');
    return response.data;
  },

  async createCheckout(plan: string) {
    const response = await apiClient.post<CheckoutResponse>(
      '/subscriptions/checkout',
      { plan }
    );
    return response.data;
  },

  /**
   * Start a one-off pass checkout. Public on the backend: a logged-out visitor
   * must supply an email so Stripe can collect and return a customer; a
   * logged-in caller's token is attached automatically and the email is omitted.
   */
  async createPublicPassCheckout(passTier: string, email?: string) {
    const response = await apiClient.post<PassCheckoutResponse>(
      '/subscriptions/checkout/pass',
      email ? { passTier, email } : { passTier }
    );
    return response.data;
  },

  /**
   * Single-use exchange of a paid Stripe session for a token pair.
   */
  async claimPass(sessionId: string) {
    const response = await apiClient.get<ClaimPassResponse>(
      '/subscriptions/passes/claim',
      { params: { session_id: sessionId } }
    );
    return response.data;
  },

  /**
   * Re-send the verification/setup mail for a paid but unverified buyer. The
   * backend picks the right mail (combined setup vs. plain verification) and
   * returns which one so the UI can describe it honestly.
   */
  async resendPassSetup() {
    const response = await apiClient.post<{
      message: string;
      kind: 'setup' | 'verification';
    }>('/subscriptions/passes/resend-setup');
    return response.data;
  },

  async cancelSubscription() {
    const response = await apiClient.post<{ message: string }>(
      '/subscriptions/cancel'
    );
    return response.data;
  },

  async reactivateSubscription() {
    const response = await apiClient.post<{ message: string }>(
      '/subscriptions/reactivate'
    );
    return response.data;
  },

  async changeSubscription(plan: string) {
    const response = await apiClient.post<{ message: string }>(
      '/subscriptions/change',
      { plan }
    );
    return response.data;
  },

  async getPortalLink() {
    const response = await apiClient.get<{ url: string }>(
      '/subscriptions/portal'
    );
    return response.data;
  },

  async getInvoices() {
    const response = await apiClient.get<Invoice[]>('/subscriptions/invoices');
    return response.data;
  },
};
